import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject, Observable } from 'rxjs';
import * as _ from 'underscore';
import { isArray, isNullOrUndefined, isUndefined } from '../../models/utils';
import { ContextMenuOption } from '../../models/context-menu-option';
import { EntityType, SearchTypeIconFirstLetter } from '../../models/enums';
import { Permissions } from '../../models/permission';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { GenericTreeNode } from './generic-tree-node';
import { CommonWithRestService } from '../../services/common/common-with-rest.service';
import { ContextMenuComponent } from '../../components/context-menu/context-menu.component';
import { SearchResult } from '../../models/search-result';
import { ViewType } from '../../models/view';
import { WorkflowStep } from '../../models/workflow';
import { Corpus, RepresentationSet, SharedState } from '../../models/corpus';
import { SortByFilter } from '../../models/grid-context';

@Component({
    standalone: false,
    selector: 'res-generic-tree',
    templateUrl: './generic-tree.component.html',
    styleUrls: ['./generic-tree.component.scss']
})
export class GenericTreeComponent implements OnInit, OnDestroy {
    @ViewChild(ContextMenuComponent) contextMenu: ContextMenuComponent;
    @Input() customClasses: string;
    @Input() automationId: string;
    @Input() initialData: Array<GenericTreeNode>;
    @Input() isMultiSelect: boolean;
    @Input() allowDeselection: boolean;
    @Input() checkPermissions = false;
    @Input() hasFilterOn = false;
    @Input() hasSelectionColumn = false;
    @Input() rowHeight = 30;
    @Input() nodeAlternativeIconCallback: (node: GenericTreeNode) => string;
    @Input() selectedNodeChangedCallback: (node: GenericTreeNode) => void;
    @Input() checkboxSelectionChangedCallback: (selectedNodes: Array<GenericTreeNode>, node: GenericTreeNode) => void;
    @Input() loadNodeDataCallback: (node: GenericTreeNode) => void;
    @Input() onContextMenuActionCallback: (node: GenericTreeNode, action: string) => void;
    @Input() preselectNodeCallback: (node: GenericTreeNode) => void;
    @Input() finishLoadingCallback: (node: GenericTreeNode) => void;
    @Input() doubleClickAction: (node: GenericTreeNode) => void;

    treeControl: FlatTreeControl<GenericTreeNode>;
    treeFlattener: MatTreeFlattener<GenericTreeNode, GenericTreeNode>;

    dataChange: BehaviorSubject<GenericTreeNode[]>;
    data: GenericTreeNode[];
    dataChangedSubscription = null;

    private checkboxSelection: SelectionModel<GenericTreeNode>;
    private expandedNodesOnInit: Array<GenericTreeNode>;

    get selectedNodes(): Array<GenericTreeNode> {
        return this.checkboxSelection.selected;
    }
    contextMenuOptions: Array<ContextMenuOption>;

    private _selectedNode: GenericTreeNode;
    private _previousManualSelectedNode: GenericTreeNode | null = null;
    public get selectedNode(): GenericTreeNode {
        return this._selectedNode;
    }
    public set selectedNode(value: GenericTreeNode) {
        if (this.selectedNode?.isManualSelection) {
            this._previousManualSelectedNode = this.selectedNode;
        }
        this._selectedNode = value;
    }

    public get previousManualSelectedNode(): GenericTreeNode | null {
        return this._previousManualSelectedNode;
    }

    private _dataSource: MatTreeFlatDataSource<GenericTreeNode, GenericTreeNode>;
    public get dataSource(): MatTreeFlatDataSource<GenericTreeNode, GenericTreeNode> {
        return this._dataSource;
    }
    public set dataSource(value: MatTreeFlatDataSource<GenericTreeNode, GenericTreeNode>) {
        this._dataSource = value;
    }

    constructor(private changeDetectorRef: ChangeDetectorRef, private restService: RestService, private commonService: CommonService,
        private elemRef: ElementRef, private xmlService: XMLService, private commonWithRestService: CommonWithRestService) {
        this.treeControl = new FlatTreeControl<GenericTreeNode>(this.getLevel, this.isExpandable);
        this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
        this.dataChange = new BehaviorSubject<GenericTreeNode[]>([]);
        this.checkboxSelection = new SelectionModel<GenericTreeNode>(true);
        this.expandedNodesOnInit = new Array<GenericTreeNode>();

        this.dataChangedSubscription = this.dataChange.subscribe(data => {
            if (data) {
                this.dataSource.data = data;
            }
        });
    }

    ngOnInit(): void {
        if (this.initialData) {
            this.setData(this.initialData);
        }
        this.commonService.pendingDeletionTags = [];
    }

    private transformer = (node: GenericTreeNode, level: number) => {
        node.level = level;
        node.onContextMenuAction = (item: GenericTreeNode, action: string) => this.onContextMenuAction(item, action);
        if (node.isExpanded && node.isExpandable && !this.treeControl.isExpanded(node) && !this.expandedNodesOnInit.includes(node)) {
            this.treeControl.expand(node);
            if (node.children.length === 0 && (node.canLoadData() || node.hasPreloadedData())) {
                this.loadNodeData(node);
            } else {
                this.expandedNodesOnInit.push(node);
            }
        }
        if (node.hasCheckbox) {
            if (node.isChecked) {
                this.checkboxSelection.select(node);
            } else {
                this.checkboxSelection.deselect(node);
            }
        }
        return node;
    }

    private getLevel = (node: GenericTreeNode) => node.level;

    private isExpandable = (node: GenericTreeNode) => node.isExpandable;

    private getChildren = (node: GenericTreeNode): Observable<GenericTreeNode[]> => node.getChildren();

    childrenAllSelected(node: GenericTreeNode, filterDisabled = false): boolean {
        let descendants;
        if (node) {
            descendants = this.treeControl.getDescendants(node).filter(n => n.hasCheckbox && (!filterDisabled || (filterDisabled && !n.isCheckboxDisabled && !n.isDisabled)));
        } else {
            descendants = this.treeControl.dataNodes.filter(n => n.hasCheckbox && (!filterDisabled || (filterDisabled && !n.isCheckboxDisabled && !n.isDisabled)));
        }
        if (!descendants.length) {
            return this.checkboxSelection.isSelected(node);
        }
        const selected = this.checkboxSelection.isSelected(node);
        const allSelected = descendants.every(child => this.checkboxSelection.isSelected(child));
        if (!selected && allSelected) {
            this.checkboxSelection.select(node);
            this.changeDetectorRef.detectChanges();
        } else if (selected && !allSelected) {
            this.checkboxSelection.deselect(node);
            this.changeDetectorRef.detectChanges();
        }
        return allSelected;
    }

    childrenPartiallySelected(node: GenericTreeNode): boolean {
        let descendants;
        if (node) {
            descendants = this.treeControl.getDescendants(node).filter(n => n.hasCheckbox);
        } else {
            descendants = this.treeControl.dataNodes.filter(n => n.hasCheckbox);
        }
        if (!descendants.length) {
            return false;
        }
        const result = descendants.some(child => this.checkboxSelection.isSelected(child));
        return result && !this.childrenAllSelected(node);
    }

    childrenCanBeSelected(node: GenericTreeNode): boolean {
        let descendants;
        if (node) {
            descendants = this.treeControl.getDescendants(node).filter(n => n.hasCheckbox && !n.isDisabled && !n.isCheckboxDisabled);
        } else {
            descendants = this.treeControl.dataNodes.filter(n => n.hasCheckbox && !n.isDisabled && !n.isCheckboxDisabled);
        }
        if (!descendants.length) {
            return false;
        }
        const result = descendants.some(child => !child.isDisabled && !child.isCheckboxDisabled);
        return result;
    }


    toggleNode(node: GenericTreeNode, toggle = true, checkboxSelectionChanged = true): void {
        if (toggle) {
            this.checkboxSelection.toggle(node);
            const nodeSelected = this.checkboxSelection.isSelected(node);
            node.isChecked = nodeSelected;
            if (node.children.length > 0) {
                this.recursivelySelectDescendants(node, nodeSelected);
            }
        }
        const isSelected = this.checkboxSelection.isSelected(node);
        const descendants = this.treeControl.getDescendants(node).filter(n => n.hasCheckbox);
        this.checkboxSelection.isSelected(node)
            ? this.checkboxSelection.select(...descendants, node)
            : this.checkboxSelection.deselect(...descendants, node);
        descendants.forEach(el => el.isChecked = isSelected);
        this.changeDetectorRef.detectChanges();
        if (this.checkboxSelectionChangedCallback && checkboxSelectionChanged) {
            this.checkboxSelectionChangedCallback(this.selectedNodes, node);
        }
    }

    recursivelySelectDescendants(node, isSelected) {
        node.children.forEach(nodeChild => {
            nodeChild.isChecked = isSelected;
            if (nodeChild.children.length > 0) {
                this.recursivelySelectDescendants(nodeChild, isSelected);
            }
        });
    }

    debounceSelect = _.debounce((node: GenericTreeNode, event: MouseEvent) => {
        this.selectNode(node, event);
    }, 500, true);

    debounceClick = (node: GenericTreeNode, event: MouseEvent) => {
        this.debounceSelect(node, event);
    }

    open(e, node, openContextMenuonLeftClick) {
        e.preventDefault();
        e.stopPropagation();

        this.setContextMenuItemsVIsibilityV1Projects(node, openContextMenuonLeftClick);
        if (this.commonService.matrixGridsSelection) {
            this.commonService.matrixGridsSelection.openFromDocumentActionBtn = false;
        }
        this.contextMenu.open(e, node, openContextMenuonLeftClick);
        this.commonWithRestService.enableDisableWordListOptions(node);
        let disable = false;
        const selectedNode = this.commonService.selectedNavigationNode;
        if (node && node.data && selectedNode && selectedNode.data &&
            node.data.handle === selectedNode.data.handle) {
            const component = this.commonService.mainContent.currentComponent;
            disable = component && component.isPageDirty && component.isPageDirty();
        }
        if (!disable) {
            node.hasContextMenuOpened = true;
        }
        this.commonService.selectedTreeNode = node;
    }

    sortChildren(e, node, sortAlphabetically) {
        e.preventDefault();
        e.stopPropagation();

        node.isSortedAlphabetically = !node.isSortedAlphabetically;
        node.requestInfo.data.count = 50;
        node.requestInfo.data.sortByFilter = sortAlphabetically ? SortByFilter.NAME : SortByFilter.DATE_CREATED;
        node.requestInfo.data.descending = node.requestInfo.data.sortByFilter === SortByFilter.NAME ? false : true;
        this.selectedNode = node;
        if (this.selectedNodeChangedCallback) {
            this.selectedNodeChangedCallback(this.selectedNode);
        }
        this.loadNodeData(node, true);
    }

    setContextMenuItemsVIsibilityV1Projects(node, openContextMenuonLeftClick): void {
        const projectParsingVersion = this.commonService?.currentProject?.attributes?.find(a => a.name === 'parsingVersion')?.value;
        if (node) {
            const dataSetsImportsCM = node?.contextMenuOptions?.filter(o => o.name?.includes('Data Set'));
            if (dataSetsImportsCM?.length > 0 && node.name === 'Imports') {
                _.each(dataSetsImportsCM, (item) => {
                    this.disableContextMenuOptionsByParsingVersion(item, projectParsingVersion);
                });
            }
            const nodes = openContextMenuonLeftClick ? node.children : [node];
            nodes.forEach(item => {
                const itemReindexCM = item?.contextMenuOptions?.find(o => o.name === 'Reindex ...');
                const itemReprocessCM = item?.contextMenuOptions?.find(o => o.name === 'Reprocess ...');
                const itemLoadFromExternalCM = item?.contextMenuOptions?.find(o => o.name === 'Load from External Area ...');
                const itemCreateDsFromSearchResultCM = item?.contextMenuOptions?.find(o => o.name === 'Create Data Set from Search Results ...');
                if (itemReindexCM && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemReindexCM, projectParsingVersion);
                } else if (itemReindexCM) {
                    itemReindexCM.enabled = !(item.data as Corpus).busy &&
                        ((item.data as Corpus).representationType === RepresentationSet.NO_INDEX || (item.data as Corpus).representationType === RepresentationSet.CONTENT_INDEX) &&
                        ((item.data as Corpus).sharedState !== SharedState.SHARED || ((item.data as Corpus).sharedState === SharedState.SHARED && (item.data as Corpus).handle.startsWith((item.data as Corpus).owner)));
                    itemReindexCM.tooltipAlwaysOn = false;
                    itemReindexCM.tooltipFunction = (item: GenericTreeNode) => (item.data as Corpus).sharedState === SharedState.SHARED && !((item.data as Corpus).handle.startsWith((item.data as Corpus).owner)) ? 'Action not allowed for a Data Set shared to this project.' : '';
                }
                if (itemReprocessCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT ||
                    (item.data as SearchResult)?.viewType === ViewType.WORKING_SET_RESULT) && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemReprocessCM, projectParsingVersion);
                } else if (itemReprocessCM) {
                    itemReprocessCM.enabled = (item.data instanceof WorkflowStep) ? ((item.data as WorkflowStep).numberResults > 0) : ((item.data as SearchResult).documentCount > 0 && (item.data.viewType != null || !isUndefined(_.findWhere((item.data as SearchResult).attributes, { name: 'viewtype' }))));
                    itemReprocessCM.tooltipAlwaysOn = false;
                    itemReprocessCM.tooltipFunction = (item: GenericTreeNode) => '';
                }
                if (itemLoadFromExternalCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemLoadFromExternalCM, projectParsingVersion);
                } else if (itemLoadFromExternalCM) {
                    itemLoadFromExternalCM.enabled = (item.data as SearchResult).documentCount > 0;
                    itemLoadFromExternalCM.tooltipAlwaysOn = false;
                    itemLoadFromExternalCM.tooltipFunction = (item: GenericTreeNode) => '';
                }
                if (itemCreateDsFromSearchResultCM && ((item.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemCreateDsFromSearchResultCM, projectParsingVersion);
                } else if (itemCreateDsFromSearchResultCM) {
                    itemCreateDsFromSearchResultCM.enabled = (item.data as SearchResult).documentCount > 0;
                    itemCreateDsFromSearchResultCM.tooltipAlwaysOn = false;
                    itemCreateDsFromSearchResultCM.tooltipFunction = (item: GenericTreeNode) => '';
                }
                this.changeDetectorRef.detectChanges();
            });
        }
    }

    onContextMenuClose() {
        if (this.commonService.selectedTreeNode) {
            this.commonService.selectedTreeNode.hasContextMenuOpened = false;
        }
    }

    selectNode = (node: GenericTreeNode, event?: MouseEvent) => {
        if ((<HTMLElement>event?.target)?.nodeName === 'SELECT' && event?.defaultPrevented) {
            return;
        }
        if (event && <HTMLElement>event.target && (<HTMLElement>event.target).classList.contains('gtn-checkbox')) {
            return;
        }
        if (event) {
            this.commonService.userSelectionClick = true;
        }
        if (isNullOrUndefined(node.automationId) || node.automationId === 'tree-node') {
            console.error('Attempting to select node without an automation id.  This will not end well.');
            console.error(node);
        }
        if (node) {
            if (!event && (!this.treeControl.dataNodes.includes(node)
                || (this.treeControl.dataNodes.findIndex(n => node.automationId === n.automationId) === -1))) {
                // manual select => check if item exists in tree
                let foundNodeAsChild = false;
                for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
                    const currentDataNode = this.treeControl.dataNodes[i];
                    if (currentDataNode?.children?.length > 0) {
                        const childIndex = currentDataNode.children.findIndex(n => node.automationId === n.automationId);
                        if (childIndex !== -1) {
                            foundNodeAsChild = true;
                            break;
                        }
                    }
                }
                if (!foundNodeAsChild) {
                    return;
                }
            }
            if (node.isExpandable && node.isExpandedOnSelect && !this.treeControl.isExpanded(node)) {
                this.loadNodeData(node, false, true);
            }
            if (node.isSelectable) {
                if (this.isMultiSelect) {
                    this.toggleNode(node);
                } else {
                    if (!this.selectedNode || this.selectedNode !== node) {
                        if (event && this.preselectNodeCallback) {
                            this.preselectNodeCallback(node);
                            return;
                        }
                        this.selectedNode = node;
                        if (this.selectedNodeChangedCallback) {
                            this.selectedNodeChangedCallback(this.selectedNode);
                        }
                        if (node.isCheckedOnSelect && node.hasCheckbox) {
                            this.toggleNode(node);
                        }
                        if (!event) {
                            let parent = node.parent;
                            while (parent && parent.isExpandable) {
                                this.treeControl.expand(parent);
                                parent = parent.parent;
                            }
                        }
                    } else if (this.allowDeselection) {
                        this.selectedNode = null;
                        if (this.selectedNodeChangedCallback) {
                            this.selectedNodeChangedCallback(this.selectedNode);
                        }
                        if (node.isCheckedOnSelect && node.hasCheckbox) {
                            this.toggleNode(node);
                        }
                    } else if (this.selectedNode === node && this.selectedNode.entityType === EntityType.workFlow && this.selectedNode.icon === 'fa-exclamation-circle') {
                        this.commonService.openErrorModal('Unexpected run error. Please rerun the workflow.');
                    }
                }
            } else if (node.isButton) {
                if (node.parent && node.parent.requestInfo && !node.getMoreOnSelectedNodeCallback) {
                    node.parent.requestInfo.data.count += 10;
                    this.loadNodeData(node.parent, true);
                } else if (this.selectedNodeChangedCallback && event && <HTMLElement>event.target && (<HTMLElement>event.target).classList.contains('gtn-content-text')) {
                    if (node.getMoreOnSelectedNodeCallback) {
                        node.isLoading = false;
                    }
                    this.selectedNodeChangedCallback(node);
                }
            }
            if (!event) {
                this.scrollElementIntoView();
            }

            if (!node.isButton && this.previousManualSelectedNode?.isManualSelection) {
                this.previousManualSelectedNode.isManualSelection = false;
                const allSelected  = this.elemRef.nativeElement.querySelectorAll('.gtn-selected');
                const currentSelectedEl = this.elemRef.nativeElement.querySelector(`[data-node-id="${node?.domId}"]`);
                allSelected?.forEach((el: HTMLElement) => {
                    if (el !== currentSelectedEl) {
                        el?.classList?.remove('gtn-selected');
                    }
                });
            }

            if (node?.isButton) {
                setTimeout(() => {
                    const element = this.elemRef.nativeElement.querySelector(`[data-node-id="${node?.prevOldNode?.domId}"]`);
                    if (element) {
                        element.classList.add('gtn-selected');
                    }
                }, node?.parent?.children?.length > 25 ? 1500 : 800);
            }

            this.treeControl.expand(node);
        }
    }

    doubleClick = (node: GenericTreeNode, event?: MouseEvent) => {
        if (this.doubleClickAction) { this.doubleClickAction(node); }
    }

    scrollElementIntoView(): void {
        setTimeout(() => {
            const element = this.elemRef.nativeElement.querySelector('.gtn-selected');
            if (element) {
                // check if outside view
                const rect = element.getBoundingClientRect();
                if (rect && (rect.top < 0 || rect.bottom > (window.innerHeight || document.documentElement.clientHeight))) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 1500);
    }

    deselectAllNodes = () => {
        if (this.selectedNodes.length > 0) {
            this.checkboxSelection.deselect(...this.selectedNodes);
            this.changeDetectorRef.detectChanges();
        }
    }

    getSelectedNodeElement(): any {
        return this.elemRef.nativeElement.querySelector('.gtn-selected');
    }

    getResponseArray(source: any, indexString: string): any[] {
        const indices = indexString.split('.');
        let array = source;
        indices.forEach(index => {
            const indexNumber = index.match(/(\d+)/);
            if (indexNumber) {
                const key = index.substr(0, index.indexOf('['));
                array = array[key];
                array = Array.isArray(array) ? array[indexNumber[0]] : array;
            } else {
                array = array[index];
            }
        });
        return array as any[];
    }

    loadNodeData = (node: GenericTreeNode, refresh = false, expandNode = false): Promise<any> => {
        let promiseToReturn = null;
        if (node) {
            if (expandNode === true) {
                this.treeControl.expand(node);
            }
            if (!node.isLoading && (node.children.length === 0 || refresh) && !this.hasFilterOn) {
                if (node.canLoadData()) {
                    node.isLoading = true;

                    // if parent isSortable inherit the sort properties from the parent
                    if (node?.data && node?.parent?.entityType === EntityType.export && node?.parent?.isSortable) {
                        node.requestInfo.data.sortByFilter = node.parent.requestInfo?.data?.sortByFilter ? node.parent.requestInfo.data.sortByFilter : SortByFilter.NAME;
                        node.requestInfo.data.descending = node.requestInfo.data.sortByFilter === SortByFilter.NAME ? false : true;
                    }

                    const optionalCall = node.secondRequestInfo ? ((response) => Promise.resolve(
                        this.restService.post(node.secondRequestInfo.endpoint, node.secondRequestInfo.data).then(optionalPromise =>
                            Object.assign(response, { second: optionalPromise })))) :
                        (response) => Promise.resolve(response);

                    promiseToReturn = this.restService.post(node.requestInfo.endpoint, node.requestInfo.data).then(optionalCall).then(response => {
                        let array = response.second ? this.getResponseArray(response, node.requestInfo.responseName) : null;
                        if (array) {
                            if (isArray(array)) {
                                const secondArray = this.getResponseArray(response.second, node.secondRequestInfo.responseName);
                                array = _.map(array,
                                    (value: any, index: number) => {
                                        if (secondArray.length <= index) { return value; }
                                        secondArray[index].attributes = _.union(value.attributes, secondArray[index].attributes);
                                        return Object.assign(value, secondArray[index]);
                                    });
                            }
                        } else {
                            array = this.getResponseArray(response, node.requestInfo.responseName);
                        }
                        if (response && isArray(array)) {
                            if (node.entityType === EntityType.searchResult && array.length < response.totalCount) {
                                const remainingSearchesCount = Math.min(10, response.totalCount - array.length);
                                this.commonService.moreButtonOriginalLabel = `${remainingSearchesCount} more ...`;
                                array.push(new GenericTreeNode({
                                    name: this.commonService.hideArchivedItems ? 'more ...' : this.commonService.moreButtonOriginalLabel,
                                    automationId: 'get-more-search-results',
                                    hasExpanderPadding: true,
                                    entityType: node.entityType,
                                    isSelectable: false,
                                    isButton: true
                                }));
                            }
                            if (node.entityType === EntityType.import && array.length < response.totalCount) {
                                const remainingImportsCount = response.totalCount - array.length;
                                this.commonService.moreDataAreasButtonOriginalLabel = `${remainingImportsCount} more ...`;
                                array.push(new GenericTreeNode({
                                    name: remainingImportsCount + ' more ...',
                                    automationId: 'get-more-imports-results',
                                    hasExpanderPadding: true,
                                    entityType: node.entityType,
                                    isSelectable: false,
                                    isButton: true,
                                    getMoreOnSelectedNodeCallback: true
                                }))
                            }
                            if (node.entityType === EntityType.export && array.length < response.totalCount) {
                                const remainingExportVolumesCount = response.totalCount - array.length;
                                array.push(new GenericTreeNode({
                                    name: remainingExportVolumesCount + ' more ...',
                                    automationId: 'get-more-export-volume-results',
                                    hasExpanderPadding: true,
                                    entityType: node.entityType,
                                    isSelectable: false,
                                    isButton: true,
                                    getMoreOnSelectedNodeCallback: true
                                }));
                            }
                            if (node.entityType === EntityType.tagView) {
                                array = array.filter(entry => entry.name !== 'Unassigned');
                            }
                            this.setNodeData(node, array);

                            // set pending tasks deletion spinner or remove deleted tags pendingDeletionTags based on response
                            if (node.name === 'Tags') {
                                this.commonService.pendingDeletionTags.slice().reverse().forEach((element, index, object) => {
                                    const tag = array.filter(entry => entry.handle === element.data.handle);
                                    if (tag?.length > 0) {
                                        const node = this.getNodeByHandle(tag[0].handle);
                                        node.isLoading = true;
                                        node.isDisabled = true;
                                        node.isTagDeletion = true;
                                    } else {
                                        this.commonService.pendingDeletionTags.splice(object.length - 1 - index, 1);
                                    }
                                });
                            }

                            // select previous node if needed
                            if (this.selectedNode && this.selectedNode.data && this.sameNodeByHandle(this.selectedNode.parent, node)) {
                                const handleField = this.selectedNode.data.id ? 'id' : 'handle';
                                const newNode = this.getNodeByField(handleField, this.selectedNode.data[handleField], node);
                                if (newNode) {
                                    this.selectNode(newNode);
                                } else {
                                    this.selectNode(node);
                                }
                            }
                        }
                        node.isLoading = false;
                        if (this.finishLoadingCallback) {
                            this.finishLoadingCallback(node);
                        }
                    }, error => {
                        node.isLoading = false;
                    });
                } else if (node.hasPreloadedData()) {
                    node.isLoading = true;
                    setTimeout(() => {
                        node.isLoading = false;
                        this.setNodeData(node, node.preloadedData);
                        // select previous node if needed
                        if (this.selectedNode && this.selectedNode.data && this.sameNodeByHandle(this.selectedNode.parent, node)) {
                            const handleField = this.selectedNode.data.id ? 'id' : 'handle';
                            const newNode = this.getNodeByField(handleField, this.selectedNode.data[handleField], node);
                            if (newNode) {
                                this.selectNode(newNode);
                            } else {
                                this.selectNode(node);
                            }
                        }
                    }, 1000);
                } else if (this.loadNodeDataCallback) {
                    this.loadNodeDataCallback(node);
                }
            }
            if (!this.treeControl.isExpanded(node)) {
                // if collapsed: close context menu if opened on a child of this node
                if (this.contextMenu) {
                    this.contextMenu.closeAllContextMenus();
                }
            }
        }
        return promiseToReturn;
    }

    disableContextMenuOptionsByParsingVersion(item: any, projectParsingVersion: string): void {
        item.enabled = projectParsingVersion === 'V2';
        item.tooltipFunction = () => projectParsingVersion === 'V1' ? 'You can no longer perform this operation for a Project using Parsing Library Version V1.' : '';
        item.tooltipAlwaysOn = projectParsingVersion === 'V1';
    }

    setData = (data: Array<GenericTreeNode>) => {
        if (data) {
            const tmpData = _.clone(data);
            if (this.checkPermissions) {
                this.checkNodePermissions(data, tmpData);
            }
            this.data = data;
            if (this.checkboxSelection) {
                this.checkboxSelection.clear();
            }
            this.treeDataUpdated();
        }
    }

    checkNodePermissions = (nodes: Array<GenericTreeNode>, tmpNodes: Array<GenericTreeNode>) => {
        if (tmpNodes) {
            tmpNodes.forEach(n => {
                let parentHasPermissions = true;
                if (n.permissions && !Permissions.checkUserPermissions(n.permissions, n.atLeastOne)) {
                    const idxToDelete = nodes.findIndex(t => t.name === n.name);
                    if (idxToDelete > -1) {
                        nodes.splice(idxToDelete, 1);
                        parentHasPermissions = false;
                    }
                }

                if (parentHasPermissions && n.children && n.children.length > 0) {
                    nodes.forEach(
                        c => c.filterBy = node => !node.permissions || (node.permissions && Permissions.checkUserPermissions(node.permissions))
                    );
                }
            });
        }
    }

    setNodeData = (node: GenericTreeNode, data: any) => {
        if (node && data) {
            if (isNullOrUndefined(node.automationId) || node.automationId === 'tree-node') {
                console.error('Attempting to set node data without an automation id.  This will not end well.');
                console.error(node);
            }
            if (!node) {
                return;
            }
            if (node?.hasCheckbox) {
                node.isChecked = this.checkboxSelection.isSelected(node);
            }
            const expandedChildren = node.isExpandable && this.treeControl.isExpanded(node) && node.children.length > 0 ?
                node.children.filter(c => c.data && c.data.handle && this.treeControl.isExpanded(c)) : [];
            if (isArray(data)) {
                if (node.entityType === EntityType.savedSearch) {
                    data.forEach(element => {
                        const searchTypeAndTask = (Object)(this.xmlService.xmlToQueryConverter(element.searchQuery, true));
                        element.iconStringContent = SearchTypeIconFirstLetter[searchTypeAndTask.searchType];
                        element.searchTask = searchTypeAndTask.query;
                        const targetName = element.attributes ? element.attributes.find(f => f.name === 'targetViewname') : null;
                        if (targetName) {
                            element.searchTask += ' in ' + targetName.value;
                        }
                    });
                }
                node.setChildren(data);
                if (expandedChildren.length > 0) {
                    node.children.forEach(child => {
                        if (expandedChildren.find(c => this.sameNodeByHandle(child, c))) {
                            child.isExpanded = true;
                        }
                    });
                }
                if (node && node.data && node.data.isBulkStep && data.length === 0) {
                    node.isExpandable = false;
                }
            } else if (node.requestInfo && node.requestInfo.responseName && isArray(data[node.requestInfo.responseName])) {
                node.setChildren(data[node.requestInfo.responseName]);
                if (expandedChildren.length > 0) {
                    node.children.forEach(child => {
                        if (expandedChildren.find(c => this.sameNodeByHandle(child, c))) {
                            child.isExpanded = true;
                        }
                    });
                }
            }
            this.treeDataUpdated();
            if (node.hasCheckbox && this.checkboxSelection.isSelected(node)) {
                this.toggleNode(node, false, false);
            }
        }
    }

    treeDataUpdated = () => {
        if (this.dataChange) {
            this.dataChange.next(this.data);
        }
    }

    isSelected = (node: GenericTreeNode): boolean => {
        if (this.isMultiSelect) {
            return this.selectedNodes.includes(node);
        } else {
            return this.selectedNode === node;
        }
    }

    isChecked = (node: GenericTreeNode): boolean => {
        return this.checkboxSelection.isSelected(node);
    }

    getNodeIcons = (node: GenericTreeNode): { icon: string, iconColor: string, iconSecond: string, iconSecondColor: string, iconThird: string, iconThirdColor: string } => {
        if (!node || (!node.icon && !this.nodeAlternativeIconCallback)) {
            return null;
        }
        let icon: { icon: string, iconColor: string, iconSecond: string, iconSecondColor: string, iconThird: string, iconThirdColor: string, bundle: any };
        if (this.nodeAlternativeIconCallback) {
            const alternativeIcon = this.nodeAlternativeIconCallback(node);
            if (alternativeIcon) {
                icon = { icon: alternativeIcon, iconColor: '', iconSecond: '', iconSecondColor: '', iconThird: '', iconThirdColor: '', bundle: null };
            }
        }
        if (!icon && node.icon) {
            let iconColorClass = node.iconColor ? node.iconColor : '';
            icon = {
                icon: (node.iconExpanded && this.treeControl.isExpanded(node)) ? node.iconExpanded : node.icon,
                iconColor: iconColorClass,
                iconSecond: node.iconSecond ? node.iconSecond : '',
                iconSecondColor: node.iconSecondColor ? node.iconSecondColor : (node.iconColor ? node.iconColor : ''),
                iconThird: node.iconThird ? node.iconThird : '',
                iconThirdColor: node.iconThirdColor ? node.iconThirdColor : (node.iconSecondColor ? node.iconSecondColor : (node.iconColor ? node.iconColor : '')),
                bundle: node.bundle
            };
        }
        return icon;
    }

    onHeaderButton = (option: ContextMenuOption, node: GenericTreeNode, event: MouseEvent) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (option && node) {
            option.click(node);
        }
    }

    onContextMenuAction(node: GenericTreeNode, action: string): void {
        if (this.onContextMenuActionCallback) {
            this.onContextMenuActionCallback(node, action);
            this.onContextMenuClose();
        } else {
            console.warn('Unable to launch menu action!');
        }
    }

    getNodeByAttr = (attrName: string, attrValue: string, startNode?: GenericTreeNode): GenericTreeNode => {
        return this.getNodeBy((item) => {
            if (item.data && item.data.attributes) {
                const attr = item.data.attributes.find(a => a.name === attrName);
                return attr && attr.value === attrValue;
            } else {
                return false;
            }
        }, startNode);
    }

    getNodeBy = (predicate: (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => boolean, startNode?: GenericTreeNode): GenericTreeNode => {
        if (startNode && startNode.children) {
            return this.getNode(predicate, startNode.children);
        } else {
            return this.getNode(predicate, this.data);
        }
    }

    getNodeByField = (fieldName: string, fieldValue: string, startNode?: GenericTreeNode): GenericTreeNode => {
        return this.getNodeBy((item) => item.data && item.data[fieldName] && item.data[fieldName] === fieldValue, startNode);
    }

    getNodeByHandle = (handle: string, startNode?: GenericTreeNode): GenericTreeNode => {
        return this.getNodeByField('handle', handle, startNode);
    }

    private getNode = (predicate: (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => boolean, nodes: GenericTreeNode[]): GenericTreeNode => {
        if (nodes) {
            const found = nodes.find(predicate);
            if (found) {
                return found;
            } else {
                for (let index = 0; index < nodes.length; index++) {
                    const foundInChildren = this.getNode(predicate, nodes[index].children);
                    if (foundInChildren) {
                        return foundInChildren;
                    }
                }
            }
        } else {
            return null;
        }
    }

    private sameNodeByHandle = (firstNode: GenericTreeNode, secondNode: GenericTreeNode): boolean => {
        return this.sameNodeByField(firstNode, secondNode, 'handle');
    }

    private sameNodeByField = (firstNode: GenericTreeNode, secondNode: GenericTreeNode, fieldName: string): boolean => {
        if (firstNode && secondNode) {
            if (firstNode === secondNode) {
                return true;
            } else {
                return fieldName && firstNode.data && secondNode.data && firstNode.data[fieldName] && firstNode.data[fieldName] === secondNode.data[fieldName];
            }
        }
        return false;
    }

    private wipeNodes(node: GenericTreeNode): void {
        node.onContextMenuAction = undefined;
        node.filterBy = undefined;
        node.children?.forEach((child) => this.wipeNodes(child));
    }

    ngOnDestroy(): void {
        if (this.dataChangedSubscription) {
            this.dataChangedSubscription.unsubscribe();
        }
        this.data?.forEach((node) => {
            this.wipeNodes(node);
        })
    }

    setTitleIfTruncated(el: HTMLElement): void {
        if (el.offsetWidth < el.scrollWidth) {
            el.setAttribute('title', el.innerHTML);
        } else {
            el.removeAttribute('title');
        }
    }

    isVerticalScrollbarVisible(): boolean {
        const treeContainer = this.elemRef.nativeElement?.querySelector('.generic-tree')
        if (treeContainer) {
            return treeContainer.scrollHeight > treeContainer.clientHeight;
        }
        return false;
    }

    updateSelectionAfterFilter(value: string): void {
        const currentSelection = [...this.checkboxSelection.selected];
        const validNodes = currentSelection.filter(selectedNode => { return selectedNode?.name.toLowerCase().includes(value.toLowerCase()) });

        // Clear and reselect only valid nodes
        this.checkboxSelection.clear();

        if (validNodes.length > 0) {
            this.checkboxSelection.select(...validNodes);
        }
        this.changeDetectorRef.detectChanges();
    }
}

